import React, { useMemo } from "react";
import ShowChartOutlinedIcon from "@mui/icons-material/ShowChartOutlined";
import { useParams } from "react-router-dom";

import RealTimeCard from "./RealTimeCard";
import { trailingAverage, useRealtimeChannels } from "./useRealtimeSeries";
import { baseChartOption, lineSeries, seriesColor } from "../charts/chartTheme";
import { EM_DASH, describe } from "../../utils/format";

/* First three categorical slots — the set that validates all-pairs in both
   themes — plus a distinct line type per axis. */
const AXES = [
  { key: "x", label: "X axis", slot: 0, lineType: "solid" },
  { key: "y", label: "Y axis", slot: 1, lineType: "dashed" },
  { key: "z", label: "Z axis", slot: 2, lineType: "dotted" },
];

const RealTimeAccelerometer = () => {
  const { watchId } = useParams();

  const paths = useMemo(
    () => ({
      channels: {
        x: `Health/${watchId}/realTime/accelerationX`,
        y: `Health/${watchId}/realTime/accelerationY`,
        z: `Health/${watchId}/realTime/accelerationZ`,
      },
      status: `Health/${watchId}/realTime/accelerometerStatusDescription`,
      progress: `Health/${watchId}/realTime/accelerometerProgress`,
    }),
    [watchId]
  );

  const { samples, channels, status, progress } = useRealtimeChannels(
    watchId,
    paths
  );

  const stats = useMemo(
    () =>
      describe(
        [...(channels.x ?? []), ...(channels.y ?? []), ...(channels.z ?? [])],
        2
      ),
    [channels]
  );

  const hasData = samples.length > 0;

  const buildOption = () =>
    baseChartOption({
      categories: samples,
      series: AXES.map((axis) =>
        lineSeries({
          name: axis.label,
          data: channels[axis.key] ?? [],
          color: seriesColor(axis.slot),
          lineType: axis.lineType,
          area: false,
          smooth: 0.2,
          width: 1.8,
          showSymbol: false,
        })
      ),
      showLegend: true,
      xName: "Sample",
      yName: "m/s²",
      tooltipFormatter: (params) => {
        if (!params?.length) return "";
        const rows = params
          .map(
            (p) =>
              `<div style="display:flex;gap:8px;justify-content:space-between">
                 <span>${p.marker} ${p.seriesName}</span>
                 <strong>${p.value ?? "—"}</strong>
               </div>`
          )
          .join("");
        return `Sample ${params[0].axisValue}${rows}`;
      },
    });

  return (
    <RealTimeCard
      title="Live accelerometer"
      icon={<ShowChartOutlinedIcon />}
      accent={seriesColor(0)}
      exportName="accelerometer"
      status={status}
      progress={progress}
      hasData={hasData}
      waitingCopy="Motion samples for all three axes appear here as the watch streams them."
      deps={[channels, samples]}
      buildOption={buildOption}
      stats={[
        ...AXES.map((axis) => ({
          label: `${axis.label} avg`,
          value: trailingAverage(channels[axis.key] ?? [], 49, 2),
          unit: "m/s²",
          meta: "Last second",
        })),
        {
          label: "Samples",
          value: hasData ? samples.length : EM_DASH,
          meta: `Range ${stats.range}`,
        },
      ]}
    />
  );
};

export default RealTimeAccelerometer;
